/**
 * vmx-add-to-cart.js
 * <vmx-product-form> Web Component.
 *
 * Responsibilities:
 *  - Variant option buttons → find matching variant → update hidden id input,
 *    price display, availability, dispatch vmx:variant:changed.
 *  - Subscribe/Save toggle → enable/disable selling_plan hidden input.
 *  - Qty stepper +/- buttons.
 *  - Form submit → POST /cart/add.js, on success dispatch vmx:cart:opened +
 *    vmx:cart:updated, on error show inline message.
 *
 * NO innerHTML mutations. Uses textContent / setAttribute / cloneNode only.
 */

class VmxProductForm extends HTMLElement {
  connectedCallback() {
    this._variants  = this._readVariants();
    this._current   = null;

    this._form       = this.querySelector('[data-form]');
    this._variantInput = this.querySelector('[data-variant-id]');
    this._priceDisplay = this.querySelector('[data-price-display]');
    this._submitBtn    = this.querySelector('[data-submit-btn]');
    this._submitLabel  = this.querySelector('[data-submit-label]');
    this._errorMsg     = this.querySelector('[data-error-msg]');
    this._soldOutMsg   = this.querySelector('[data-sold-out-msg]');
    this._sellingPlan  = this.querySelector('[data-selling-plan]');

    this._bindOptions();
    this._bindModeToggle();
    this._bindQtyStepper();

    if (this._form) {
      this._form.addEventListener('submit', this._onSubmit.bind(this));
    }

    // Initialise from the current variant id
    const initialId = this._variantInput ? parseInt(this._variantInput.value, 10) : null;
    if (initialId) {
      this._current = this._variants.find((v) => v.id === initialId) || null;
    }
  }

  _readVariants() {
    const script = this.querySelector('[data-product-variants]');
    if (!script) return [];
    try { return JSON.parse(script.textContent || '[]'); } catch { return []; }
  }

  _bindOptions() {
    const optionBtns = this.querySelectorAll('[data-option-name]');
    optionBtns.forEach((btn) => {
      btn.addEventListener('click', () => this._selectOption(btn));
    });
  }

  _selectOption(clicked) {
    const name  = clicked.dataset.optionName;
    const value = clicked.dataset.optionValue;

    // Update active state for this option group
    this.querySelectorAll(`[data-option-name="${CSS.escape(name)}"]`).forEach((btn) => {
      btn.classList.toggle('is-selected', btn === clicked);
      btn.setAttribute('aria-pressed', String(btn === clicked));
    });

    // Collect all currently selected option values
    const selected = {};
    this.querySelectorAll('[data-option-name].is-selected').forEach((btn) => {
      selected[btn.dataset.optionName] = btn.dataset.optionValue;
    });

    // Find the matching variant
    const match = this._variants.find((v) => {
      return Object.entries(selected).every(([, val], i) => v.options[i] === val);
    });

    this._applyVariant(match || null);
  }

  _applyVariant(variant) {
    if (!variant) return;
    this._current = variant;

    if (this._variantInput) {
      this._variantInput.value = variant.id;
    }

    // Update price display (simple text; full money formatting deferred to server)
    if (this._priceDisplay) {
      const price = (variant.price / 100).toFixed(2);
      const comparePrice = variant.compareAtPrice ? (variant.compareAtPrice / 100).toFixed(2) : null;
      // Clear and rebuild using textContent / createElement — no innerHTML
      const priceEl = this._priceDisplay.querySelector('.vmx-price__current');
      if (priceEl) priceEl.textContent = `£${price}`;
      const compareEl = this._priceDisplay.querySelector('.vmx-price__compare');
      if (compareEl && comparePrice) {
        compareEl.textContent = `£${comparePrice}`;
        compareEl.hidden = false;
      } else if (compareEl) {
        compareEl.hidden = true;
      }
    }

    // Update submit button label
    const available = variant.available;
    if (this._submitBtn)  this._submitBtn.disabled = !available;
    if (this._submitLabel) {
      this._submitLabel.textContent = available
        ? `Add to bag — £${(variant.price / 100).toFixed(2)}`
        : 'Sold out';
    }
    if (this._soldOutMsg) this._soldOutMsg.hidden = available;

    // Dispatch variant changed event (gallery listens)
    document.dispatchEvent(
      new CustomEvent('vmx:variant:changed', {
        bubbles: true,
        detail: {
          variantId: variant.id,
          imageId:   variant.imageId,
          imageSrc:  variant.imageSrc,
          imageAlt:  variant.imageAlt,
        },
      }),
    );
  }

  _bindModeToggle() {
    const radios     = this.querySelectorAll('[data-mode]');
    const modeEl     = this.querySelector('[data-mode-toggle]');
    const planSelect = this.querySelector('[data-plan-select]');
    const plansBox   = this.querySelector('[data-plans]');

    // Apply a penny price to the main price, CTA label, and sticky bar.
    const applyPrice = (pennies) => {
      if (!pennies) return;
      const f = '£' + (parseInt(pennies, 10) / 100).toFixed(2);
      if (this._priceDisplay) {
        const cur = this._priceDisplay.querySelector('.vmx-price__current');
        if (cur) cur.textContent = f;
      }
      if (this._submitLabel && /£[\d.]+/.test(this._submitLabel.textContent)) {
        this._submitLabel.textContent = this._submitLabel.textContent.replace(/£[\d.]+/, f);
      }
      const sp = document.querySelector('[data-sticky-price]');
      if (sp) sp.textContent = f;
    };

    const selectedPlanPrice = () => {
      if (!planSelect) return null;
      const opt = planSelect.options[planSelect.selectedIndex];
      return opt ? opt.dataset.planPrice : null;
    };

    const setMode = (mode) => {
      const isSub = mode === 'subscribe';
      if (modeEl) modeEl.dataset.activeMode = mode;
      if (plansBox) plansBox.hidden = !isSub;
      if (this._sellingPlan) {
        this._sellingPlan.disabled = !isSub;
        if (isSub && planSelect) this._sellingPlan.value = planSelect.value;
      }
      applyPrice(isSub
        ? (selectedPlanPrice() || (modeEl && modeEl.dataset.priceSub))
        : (modeEl && modeEl.dataset.priceOnetime));
    };

    radios.forEach((radio) => {
      radio.addEventListener('change', () => { if (radio.checked) setMode(radio.value); });
    });

    // Changing the delivery frequency updates the submitted plan + price.
    if (planSelect) {
      planSelect.addEventListener('change', () => {
        if (this._sellingPlan) this._sellingPlan.value = planSelect.value;
        applyPrice(selectedPlanPrice());
      });
    }
  }

  _bindQtyStepper() {
    const input = this.querySelector('[data-qty-input]');
    const dec   = this.querySelector('[data-qty-dec]');
    const inc   = this.querySelector('[data-qty-inc]');
    if (!input) return;

    if (dec) dec.addEventListener('click', () => {
      const val = Math.max(1, parseInt(input.value, 10) - 1);
      input.value = val;
    });
    if (inc) inc.addEventListener('click', () => {
      const max = parseInt(input.max || '10', 10);
      const val = Math.min(max, parseInt(input.value, 10) + 1);
      input.value = val;
    });
  }

  async _onSubmit(e) {
    e.preventDefault();
    if (!this._form) return;

    const data     = new FormData(this._form);
    const btn      = this._submitBtn;
    const label    = this._submitLabel;
    const original = label ? label.textContent : 'Add to bag';

    if (btn) btn.disabled = true;
    if (label) label.textContent = 'Adding…';
    this._clearError();

    try {
      const resp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: data,
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.description || `Cart error (${resp.status})`);
      }

      document.dispatchEvent(new CustomEvent('vmx:cart:updated', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('vmx:cart:opened',  { bubbles: true }));

    } catch (err) {
      this._showError(err.message || 'Could not add to cart. Please try again.');
    } finally {
      if (btn)   btn.disabled = !(this._current && this._current.available);
      if (label) label.textContent = original;
    }
  }

  _showError(msg) {
    if (!this._errorMsg) return;
    this._errorMsg.textContent = msg;
    this._errorMsg.hidden = false;
  }

  _clearError() {
    if (!this._errorMsg) return;
    this._errorMsg.textContent = '';
    this._errorMsg.hidden = true;
  }
}

if (!customElements.get('vmx-product-form')) {
  customElements.define('vmx-product-form', VmxProductForm);
}

/**
 * Quick-add buttons on product cards.
 * Delegates click on [data-quick-add] → POST /cart/add.js → dispatch events.
 * Quantity comes from data-quick-add-qty (kept in sync by the bundle-chip
 * listener below). Inner DOM (icon + spans) is preserved across the
 * loading/error states by cloning child nodes — never innerHTML, to keep
 * the static analyser happy and avoid any XSS-shaped vector even though
 * the content is server-rendered.
 */
function initQuickAdd() {
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();

    const variantId = btn.dataset.quickAdd;
    if (!variantId) return;

    const quantity = Math.max(1, parseInt(btn.dataset.quickAddQty || '1', 10));

    // Snapshot the button's DOM (icon + spans) so we can restore it later.
    const originalNodes = Array.from(btn.childNodes).map((n) => n.cloneNode(true));
    btn.disabled = true;
    btn.replaceChildren(document.createTextNode(`Adding ${quantity}…`));

    try {
      const body = new FormData();
      body.set('id', variantId);
      body.set('quantity', String(quantity));

      const resp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });

      if (!resp.ok) throw new Error(`${resp.status}`);

      document.dispatchEvent(new CustomEvent('vmx:cart:updated', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('vmx:cart:opened',  { bubbles: true }));
    } catch {
      btn.replaceChildren(document.createTextNode('Error — try again'));
    } finally {
      setTimeout(() => {
        btn.disabled = false;
        btn.replaceChildren(...originalNodes);
      }, 2000);
    }
  });
}

/**
 * Bundle-chip selector — keeps qty/total in sync between chip and CTA.
 * One delegated change handler at the document root, no per-card listener.
 */
function initBundleChips() {
  document.addEventListener('change', (e) => {
    const input = e.target.closest('.vmx-product-card__qty-input');
    if (!input) return;

    const fieldset = input.closest('[data-qty-chips]');
    const card     = input.closest('.vmx-product-card');
    if (!fieldset || !card) return;

    // Mark the selected chip; clear siblings.
    fieldset.querySelectorAll('.vmx-product-card__qty-chip').forEach((c) => {
      c.classList.toggle('is-selected', c.contains(input));
    });

    // Sync the quick-add button payload + visible total.
    const btn   = card.querySelector('[data-quick-add]');
    const total = card.querySelector('[data-quick-add-total]');
    if (btn)   btn.dataset.quickAddQty = input.dataset.qty || '1';
    if (total && input.dataset.total) total.textContent = input.dataset.total;
  });
}

function vmxInitCardWiring() {
  initQuickAdd();
  initBundleChips();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', vmxInitCardWiring);
} else {
  vmxInitCardWiring();
}
